/* eslint-disable react-hooks/exhaustive-deps, react/no-array-index-key, consistent-return */
import { FormattedMessage } from 'react-intl'
import Button from 'ui/Button'
import { GridType } from 'utils/constants/gridType'

import AddHoldingModal from 'components/AddHoldingModal/AddHoldingModal'
import GridSelector from 'components/GridSelector'
import { Helmet } from 'components/Helmet'
import HoldingsList from 'components/HoldingsList'
import InfiniteScrollRef from 'components/InfiniteScrollRef'
import SortBy from 'components/SortBy'
import Tooltip from 'components/Tooltip'
import { ZeroState, ZeroStateType } from 'components/ZeroStateV2'
import ZeroStateLinks from 'utils/constants/zerostatelinks'

import CWLoader from 'components/CWLoader'
import useHoldingFilters from 'components/HoldingFilters/useHoldingFilters'
import { AssistanceCardLarge } from 'components/OnboardingModal/components/Assistance'
import { SortCriteria } from 'components/SortBy/SortBy'
import { InputContainer, Title } from 'components/ViewHeader/ViewHeader.styles'
import { isActingAsInvestorGroup } from 'selectors/auth'
import IconButton from 'ui/IconButton'
import Input from 'ui/Input'
import layout from 'utils/constants/zerostatelayout'
import { useAppSelector } from 'utils/hooks/reduxToolkit'
import useOnClickRedirect from 'utils/hooks/useOnClickRedirect'
import { AssistanceCardType } from 'utils/types/onboardingModal'
import { useHighlightText } from 'utils/hooks/useHighlightText'

import HoldingSubjectCard from 'components/HoldingSubjectCard/HoldingSubjectCard'
import HoldingSubjectRowCard from 'components/HoldingSubjectCard/HoldingSubjectRowCard'
import {
  BasicSubjectFragment,
  SortDirection,
} from 'types/graphql-schemas/graphql'
import FiltersDrawer from './FiltersDrawer'
import { getLabelForSortCriteria } from './helpers'
import { SortByOptions, useCompanyIndex } from './useCompanyIndex'

import * as Styles from './Holdings.styles'

const HoldingIndex = () => {
  const {
    endOfPageRef,
    intl,
    holdings,
    isFetchingNextPage,
    currentFilters,
    gridType,
    isLoading,
    isModalOpen,
    showZeroState,
    showNoResults,
    showCompanyList,
    onChangeGridType,
    onDismissMessage,
    search,
    debouncedSearch,
    onChangeSearch,
    setCurrentFilters,
    setIsModalOpen,
    onChangeSortBy,
    isHoldingFilterDrawerOpen,
    onToggleFiltersDrawer,
    onClearFilters,
    isMobile,
  } = useCompanyIndex()

  const { isFilterApplied, onOpenDrawerFiltersHandler } = useHoldingFilters({
    currentFilters,
    onChangeFilters: setCurrentFilters,
    onToggleFiltersDrawer,
  })

  const isInvestor = useAppSelector(isActingAsInvestorGroup)

  const { handleOnHoldingSubjectClick } = useOnClickRedirect()

  const sortCriteria: SortCriteria[] = [
    {
      id: `${SortByOptions.NAME}_${SortDirection.Asc}`,
      field: 'name',
      direction: SortDirection.Asc,
      label: getLabelForSortCriteria(intl, 'name', SortDirection.Asc),
    },
    {
      id: `${SortByOptions.NAME}_${SortDirection.Desc}`,
      field: 'name',
      direction: SortDirection.Desc,
      label: getLabelForSortCriteria(intl, 'name', SortDirection.Desc),
    },
  ]

  useHighlightText(
    {
      elementClass: '.holding-name',
      text: search,
    },
    [isLoading, isFetchingNextPage, search]
  )

  const displayHoldingCard = (subject: BasicSubjectFragment) => {
    return gridType === GridType.GRID ? (
      <HoldingSubjectCard
        key={subject.id}
        subject={subject}
        onClick={() => handleOnHoldingSubjectClick(subject)}
        // hideAddToPortfolioButton={isInvestor} // Revert back to this tho re-enable the button
        hideAddToPortfolioButton
      />
    ) : (
      <HoldingSubjectRowCard
        key={subject.id}
        subject={subject}
        onClick={() => handleOnHoldingSubjectClick(subject)}
        // hideAddToPortfolioButton={isInvestor} // Revert back to this tho re-enable the button
        hideAddToPortfolioButton
      />
    )
  }

  return (
    <>
      <Helmet>
        <title>
          {intl.formatMessage({
            id: isModalOpen ? 'metaTitles.addNew' : 'metaTitles.discover',
          })}
        </title>
      </Helmet>

      {!isInvestor && (
        <Styles.HeaderContainer>
          <Tooltip
            id="btn-add-company"
            text={
              <>
                <p>
                  {intl.formatMessage({ id: 'holdings.addHoldingTooltip' })}
                </p>
                <p>{intl.formatMessage({ id: 'holdings.addHoldingToCW' })}</p>
              </>
            }
            place="right"
            topOffset={-10}
            delayShow={500}
          >
            <Button
              id="btn-add-company"
              uppercase
              primary
              add
              onClick={() => {
                setIsModalOpen(true)
              }}
            >
              {intl.formatMessage({
                id: isMobile ? 'holdings.addHolding' : 'holdings.addNew',
              })}
            </Button>
          </Tooltip>

          <Styles.SearchContainer>
            <InputContainer>
              <Input
                placeholder={intl.formatMessage({
                  id: 'general.searchEllipsis',
                })}
                value={search}
                onChange={onChangeSearch}
                icon={['far', 'search']}
                iconFontSize="1.5rem"
                isCleanable
              />
            </InputContainer>
          </Styles.SearchContainer>
        </Styles.HeaderContainer>
      )}

      <Styles.TitleContainer paddingTop={isInvestor ? '0rem' : '2rem'}>
        <Title>
          {intl.formatMessage({
            id: isInvestor
              ? 'holdings.holdings'
              : 'holdings.exploreOpportunities',
          })}
        </Title>

        <Styles.Row>
          {!isInvestor && (
            <>
              <Styles.MobileFilterContainer>
                <IconButton
                  onClick={onOpenDrawerFiltersHandler}
                  isActive={isFilterApplied}
                  buttonType="square"
                  icon="filter"
                  border={isFilterApplied}
                  iconFontSize="1.4rem"
                />
              </Styles.MobileFilterContainer>
              <div id="filter-button-portal" />
            </>
          )}
          <SortBy
            sortCriteria={sortCriteria}
            onChange={onChangeSortBy}
            initialSortBy={{
              id: `${currentFilters.orderBy}_${currentFilters.direction}`,
              field: currentFilters.orderBy!,
              direction: currentFilters.direction!,
              label: getLabelForSortCriteria(
                intl,
                currentFilters.orderBy!,
                currentFilters.direction!
              ),
            }}
            showSortDirectionToggle={false}
            growOptionItems
            containerPadding="0rem"
            alignDropdownContent="right"
            mobileEnabled
          />

          {isInvestor ? (
            <Styles.SearchContainer>
              <InputContainer>
                <Input
                  placeholder={intl.formatMessage({
                    id: 'general.searchEllipsis',
                  })}
                  value={search}
                  onChange={onChangeSearch}
                  icon={['far', 'search']}
                  iconFontSize="1.5rem"
                  isCleanable
                />
              </InputContainer>
            </Styles.SearchContainer>
          ) : (
            <Styles.DesktopFilterContainer>
              <Button
                onClick={onOpenDrawerFiltersHandler}
                link
                icon={['fal', 'filter']}
                className="filter-button"
                active={isFilterApplied}
              >
                <FormattedMessage id="companyList.filters" />
              </Button>
            </Styles.DesktopFilterContainer>
          )}

          <Styles.GridSelectorContainer>
            <GridSelector
              initialGridType={gridType}
              onChange={onChangeGridType}
            />
          </Styles.GridSelectorContainer>
        </Styles.Row>
      </Styles.TitleContainer>

      <Styles.SearchContainerMobile>
        <Input
          placeholder={intl.formatMessage({ id: 'general.searchEllipsis' })}
          value={search}
          onChange={onChangeSearch}
          icon={['far', 'search']}
          iconFontSize="1.5rem"
        />
      </Styles.SearchContainerMobile>

      {!isInvestor && (
        <FiltersDrawer
          isOpen={isHoldingFilterDrawerOpen}
          onClose={onToggleFiltersDrawer}
          currentFilters={currentFilters}
          onChangeFilters={setCurrentFilters}
        />
      )}

      {isLoading ? (
        <Styles.InitialLoaderWrapper>
          <CWLoader
            text={intl.formatMessage({
              id: 'holdings.loadingHoldings',
            })}
          />
        </Styles.InitialLoaderWrapper>
      ) : (
        <>
          {!!showZeroState && !isInvestor && (
            <Styles.ZeroStateContainer>
              <ZeroState
                className="holdings-zero-state"
                padding="2.5rem"
                type={ZeroStateType.HOLDINGS}
                isChatWithSupportEnabled
                linkToGuide={ZeroStateLinks.ADD_FIRST_COMPANY}
                onDismissMessage={onDismissMessage}
                layout={layout.HORIZONTAL}
                isNeedHelpEnabled={false}
              />
            </Styles.ZeroStateContainer>
          )}

          {!!showCompanyList && !showNoResults && (
            <HoldingsList
              key={
                holdings?.length ? `notEmpty_${gridType}` : `empty_${gridType}`
              }
              gridType={gridType}
            >
              {holdings?.map(displayHoldingCard)}
            </HoldingsList>
          )}

          {isFetchingNextPage && (
            <Styles.LoaderWrapper>
              <CWLoader
                text={intl.formatMessage({
                  id: 'holdings.loadingMoreHoldings',
                })}
                logoSize="50px"
              />
            </Styles.LoaderWrapper>
          )}

          {!!showNoResults && (
            <Styles.NoResults>
              {isFilterApplied ? (
                <ZeroState
                  padding="5rem 0 0 0"
                  type={ZeroStateType.NO_HOLDINGS_FOUND}
                  maxWidth="50rem"
                  zeroStateLink
                  callToAction={onClearFilters}
                  callToActionText={intl.formatMessage({
                    id: 'zeroState.no_holdings_found.clear_filters',
                  })}
                />
              ) : (
                <ZeroState
                  padding="5rem 0 0 0"
                  type={ZeroStateType.NO_HOLDINGS_RESULTS}
                  maxWidth="50rem"
                  intlValues={{ search: debouncedSearch }}
                />
              )}
            </Styles.NoResults>
          )}
        </>
      )}

      <InfiniteScrollRef ref={endOfPageRef} offset="0" />

      <AddHoldingModal
        isModalOpen={isModalOpen}
        onHideModal={() => setIsModalOpen(false)}
      />
      <AssistanceCardLarge type={AssistanceCardType.HOLDING} />
    </>
  )
}

export default HoldingIndex
